import React, { Children, cloneElement, useMemo } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import { useFormikContext } from 'formik';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import identity from 'lodash/identity';
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';

// GLOBAL VARIABLES
import { FORM_TABLE } from 'defaults.js';

// GLOBAL FUNCTIONS
import { controlName, doCallback } from 'functions';

// FORM COMPONENTS
import Col from 'core/form/parts/Col';
import Row from 'core/form/parts/Row';
import Button from 'core/tools/Button';

// LOCAL COMPONENTS
import DeleteButton from '../tools/DeleteButton';

//STYLES
import styles from '../table.module.scss';

// MAIN COMPONENT
const TableBody = ({
  children,
  name,
  itemLabel = 'Item',
  min = FORM_TABLE.min || 0,
  buttons = [],
  itemWrapper = identity,
  disabled,
  plaintext,
  allowDelete,
  handleDelete,
  readOnly
}) => {
  // FORMIK CONTEXT
  const { values } = useFormikContext() || {};

  // MEMOS
  const value = useMemo(() => cloneDeep(get(values, name)) || [], [name, values]);

  let count = 0;
  // RENDER
  return (
    <div className={styles.body}>
      {value.map((item, i) => {
        if (!item?.isDeleted) {
          count += 1;
        }
        return itemWrapper(
          <div key={i} className={clsx(item?.isDeleted === true && 'd-none', styles.item)}>
            <Row>
              <Col className={styles.numberCol} cols={{ xs: 24 }}>
                <div className={styles.itemNumber}>
                  <span className={styles.prefix}>{itemLabel}</span> <span>{count}</span>
                </div>
              </Col>
              <Col>
                <Row>
                  {Children.map(isFunction(children) ? children(item, i) : children, (child, c) =>
                    child && cloneElement(child, {
                      key: c,
                      name: child.props.name ? controlName(child.props.name, name, i) : undefined,
                      label: Object.assign(
                        {
                          col: {
                            className: styles.controlLabel
                          }
                        },
                        isObject(child.props.label)
                          ? child.props.label
                          : {
                              label: child.props.label
                            }
                      ),
                      cols: {
                        ...child.props.cols,
                        lg: child.props.colWidth
                      },
                      formGroup: Object.assign({}, child.props.formGroup, {
                        className: clsx(child.props.formGroup?.className, styles.controlFormGroup)
                      }),
                      fullWidth: true,
                      disabled: disabled || child.props.disabled,
                      plaintext: plaintext || child.props.plaintext,
                      readOnly: readOnly || child.props.readOnly
                    })
                  )}
                </Row>
              </Col>
              <Col
                className={clsx(
                  styles.buttonCol,
                  styles[`buttonCol-${buttons.length + (allowDelete ? 1 : 0)}`],
                  'table-button-col'
                )}
                cols={{ xs: 24 }}
              >
                {buttons.map(({ as: ButtonComponent = Button, ...button } = {}) => (
                  <ButtonComponent
                    {...button}
                    index={i}
                    className={clsx(styles.button, button.className)}
                    icon={{
                      use: button.icon,
                      order: 1
                    }}
                    onClick={e => doCallback(button.onClick, e, i)}
                    fullWidth={false}
                    item={item}
                    {...FORM_TABLE.itemButton}
                  />
                ))}
                {allowDelete && (
                  <DeleteButton index={i} name={name} itemLabel={itemLabel} item={item} handleDelete={handleDelete} />
                )}
              </Col>
            </Row>
          </div>
        , i);
      })}
    </div>
  );
};
// EXPORT
export default TableBody;
